/* eslint-disable max-len */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  FormHelperText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { ContainerFormFormik } from 'components/shared/container_form_formik';
import ButtonForm from 'components/buttons/button-form';
import { useStep7 } from './useStep7';
import { ProgramaAssistenciaTecnica } from '../../../../../../stores/forms/formularioAgricultura.store';

const PARCEIROS = [
  'Cooperativas',
  'Embrapa',
  'Empaer',
  'Empresas Privadas',
  'IFMT',
  'Incra',
  'ONGs',
  'Sebrae',
  'Senar',
  'Universidades Privadas',
  'Universidades Públicas',
  'Nenhuma',
].sort();

const TIPOS_COMUNIDADE = [
  'Acampamento Rural / Pré-Assentamento',
  'Agricultores Familiares',
  'Assentamento de Reforma Agrária (INCRA ou Intermat)',
  'Extrativistas',
  'Indígena',
  'Pantaneira',
  'Pescadores Artesanais',
  'Quilombola',
  'Retireiros do Araguaia',
  'Ribeirinha',
].sort();

const Step7: React.FC = () => {
  const {
    podeAvancar,
    handleAvancar,
    handleVoltar,
    stepAtual,
    totalSteps,
    programas,
    adicionarPrograma,
    removerPrograma,
  } = useStep7();

  // Estados do formulário
  const [programa, setPrograma] = useState('');
  const [parceiros, setParceiros] = useState<string[]>([]);
  const [beneficiados, setBeneficiados] = useState('');
  const [tiposComunidade, setTiposComunidade] = useState<string[]>([]);

  // Estados de validação
  const [errors, setErrors] = useState({
    programa: '',
    parceiros: '',
    beneficiados: '',
    tiposComunidade: '',
  });

  const [touched, setTouched] = useState({
    programa: false,
    parceiros: false,
    beneficiados: false,
    tiposComunidade: false,
  });

  // Validação do campo Programa
  const validarPrograma = (value: string) => {
    if (!value.trim()) {
      return 'Campo obrigatório';
    }
    if (value.length > 50) {
      return 'Máximo de 50 caracteres';
    }
    return '';
  };

  // Validação do campo Parceiros
  const validarParceiros = (values: string[]) => {
    if (values.length === 0) {
      return 'Campo obrigatório';
    }
    return '';
  };

  // Validação do campo Beneficiados
  const validarBeneficiados = (value: string) => {
    if (!value.trim()) {
      return 'Campo obrigatório';
    }
    const num = parseInt(value);
    if (isNaN(num) || num <= 0) {
      return 'O valor deve ser maior que 0';
    }
    return '';
  };

  // Validação do campo Tipo da Comunidade
  const validarTiposComunidade = (values: string[]) => {
    if (values.length === 0) {
      return 'Campo obrigatório';
    }
    return '';
  };

  // Handler para mudança no campo Programa
  const handleProgramaChange = (value: string) => {
    setPrograma(value);
    if (touched.programa) {
      setErrors(prev => ({ ...prev, programa: validarPrograma(value) }));
    }
  };

  // Handler para blur no campo Programa
  const handleProgramaBlur = () => {
    setTouched(prev => ({ ...prev, programa: true }));
    setErrors(prev => ({ ...prev, programa: validarPrograma(programa) }));
  };

  // Handler para mudança em Parceiros
  const handleParceiroChange = (parceiro: string) => {
    let newParceiros: string[];

    if (parceiro === 'Nenhuma') {
      if (parceiros.includes('Nenhuma')) {
        newParceiros = [];
      } else {
        newParceiros = ['Nenhuma'];
      }
    } else {
      if (parceiros.includes('Nenhuma')) {
        newParceiros = [parceiro];
      } else {
        if (parceiros.includes(parceiro)) {
          newParceiros = parceiros.filter(p => p !== parceiro);
        } else {
          newParceiros = [...parceiros, parceiro];
        }
      }
    }

    setParceiros(newParceiros);
    if (touched.parceiros) {
      setErrors(prev => ({ ...prev, parceiros: validarParceiros(newParceiros) }));
    }
  };

  // Handler para mudança em Beneficiados
  const handleBeneficiadosChange = (value: string) => {
    // Remove zeros à esquerda e aceita apenas números
    const sanitized = value.replace(/^0+/, '') || '';
    if (sanitized === '' || /^\d{1,6}$/.test(sanitized)) {
      setBeneficiados(sanitized);
      if (touched.beneficiados) {
        setErrors(prev => ({ ...prev, beneficiados: validarBeneficiados(sanitized) }));
      }
    }
  };

  // Handler para blur no campo Beneficiados
  const handleBeneficiadosBlur = () => {
    setTouched(prev => ({ ...prev, beneficiados: true }));
    setErrors(prev => ({ ...prev, beneficiados: validarBeneficiados(beneficiados) }));
  };

  // Handler para mudança em Tipo da Comunidade
  const handleTipoComunidadeChange = (tipo: string) => {
    const newTipos = tiposComunidade.includes(tipo)
      ? tiposComunidade.filter(t => t !== tipo)
      : [...tiposComunidade, tipo];

    setTiposComunidade(newTipos);
    if (touched.tiposComunidade) {
      setErrors(prev => ({ ...prev, tiposComunidade: validarTiposComunidade(newTipos) }));
    }
  };

  // Verificar se o formulário é válido
  const isFormularioValido = () => {
    return (
      validarPrograma(programa) === '' &&
      validarParceiros(parceiros) === '' &&
      validarBeneficiados(beneficiados) === '' &&
      validarTiposComunidade(tiposComunidade) === ''
    );
  };

  // Handler para adicionar programa
  const handleAdicionar = () => {
    // Marcar todos como touched
    setTouched({
      programa: true,
      parceiros: true,
      beneficiados: true,
      tiposComunidade: true,
    });

    // Validar todos os campos
    const errosValidacao = {
      programa: validarPrograma(programa),
      parceiros: validarParceiros(parceiros),
      beneficiados: validarBeneficiados(beneficiados),
      tiposComunidade: validarTiposComunidade(tiposComunidade),
    };

    setErrors(errosValidacao);

    // Se não houver erros, adicionar o programa
    if (Object.values(errosValidacao).every(erro => erro === '')) {
      const novoPrograma: ProgramaAssistenciaTecnica = {
        id: Date.now().toString(),
        programa: programa,
        parceiros: parceiros,
        beneficiados: parseInt(beneficiados),
        tipos_comunidade: tiposComunidade,
      };

      adicionarPrograma(novoPrograma);

      // Limpar o formulário
      setPrograma('');
      setParceiros([]);
      setBeneficiados('');
      setTiposComunidade([]);
      setTouched({
        programa: false,
        parceiros: false,
        beneficiados: false,
        tiposComunidade: false,
      });
      setErrors({
        programa: '',
        parceiros: '',
        beneficiados: '',
        tiposComunidade: '',
      });
    }
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          mt: 4,
          mb: 4,
          fontSize: '24px',
          fontWeight: 400,
          textAlign: 'center'
        }}
      >
        Programas e Assistência Técnica
      </Typography>

      {/* SEÇÃO: Adicionar Novo Registro */}
      <ContainerFormFormik title='ADICIONAR NOVO REGISTRO'>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} gap={3} display={'flex'} flexDirection={'column'}>

          {/* Campo Programa */}
          <FormControl fullWidth error={touched.programa && !!errors.programa}>
            <FormLabel sx={{ color: '#333', fontWeight: 'bold', mb: 1 }}>
              <Box component="span" sx={{ color: 'red', fontSize: '15px', mr: 1 }}>*</Box>
              Programa
            </FormLabel>
            <TextField
              variant='outlined'
              placeholder='Digite o nome do programa...'
              value={programa}
              onChange={(e) => handleProgramaChange(e.target.value)}
              onBlur={handleProgramaBlur}
              error={touched.programa && !!errors.programa}
              inputProps={{ maxLength: 50 }}
              helperText={
                touched.programa && errors.programa
                  ? errors.programa
                  : `${programa.length}/50 caracteres`
              }
            />
          </FormControl>

          {/* Campo Parceiro */}
          <FormControl fullWidth error={touched.parceiros && !!errors.parceiros}>
            <FormLabel sx={{ color: '#333', fontWeight: 'bold', mb: 1 }}>
              <Box component="span" sx={{ color: 'red', fontSize: '15px', mr: 1 }}>*</Box>
              Parceiro
            </FormLabel>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              border: touched.parceiros && errors.parceiros ? '1px solid #d32f2f' : '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              backgroundColor: '#fafafa'
            }}>
              {PARCEIROS.map((parceiro) => (
                <FormControlLabel
                  key={parceiro}
                  control={
                    <Checkbox
                      checked={parceiros.includes(parceiro)}
                      onChange={() => handleParceiroChange(parceiro)}
                    />
                  }
                  label={parceiro}
                />
              ))}
            </Box>
            {touched.parceiros && errors.parceiros && (
              <FormHelperText error>{errors.parceiros}</FormHelperText>
            )}
          </FormControl>

          {/* Campo Beneficiados */}
          <FormControl fullWidth error={touched.beneficiados && !!errors.beneficiados}>
            <FormLabel sx={{ color: '#333', fontWeight: 'bold', mb: 1 }}>
              <Box component="span" sx={{ color: 'red', fontSize: '15px', mr: 1 }}>*</Box>
              Beneficiados (Quantidade)
            </FormLabel>
            <TextField
              variant='outlined'
              placeholder='0'
              type='number'
              value={beneficiados}
              onChange={(e) => handleBeneficiadosChange(e.target.value)}
              onBlur={handleBeneficiadosBlur}
              error={touched.beneficiados && !!errors.beneficiados}
              inputProps={{ min: 1, max: 999999 }}
              helperText={touched.beneficiados && errors.beneficiados ? errors.beneficiados : ''}
            />
          </FormControl>

          {/* Campo Tipo da Comunidade */}
          <FormControl fullWidth error={touched.tiposComunidade && !!errors.tiposComunidade}>
            <FormLabel sx={{ color: '#333', fontWeight: 'bold', mb: 1 }}>
              <Box component="span" sx={{ color: 'red', fontSize: '15px', mr: 1 }}>*</Box>
              Tipo da Comunidade
            </FormLabel>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              border: touched.tiposComunidade && errors.tiposComunidade ? '1px solid #d32f2f' : '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              backgroundColor: '#fafafa'
            }}>
              {TIPOS_COMUNIDADE.map((tipo) => (
                <FormControlLabel
                  key={tipo}
                  control={
                    <Checkbox
                      checked={tiposComunidade.includes(tipo)}
                      onChange={() => handleTipoComunidadeChange(tipo)}
                    />
                  }
                  label={tipo}
                  sx={{ minWidth: '280px' }}
                />
              ))}
            </Box>
            {touched.tiposComunidade && errors.tiposComunidade && (
              <FormHelperText error>{errors.tiposComunidade}</FormHelperText>
            )}
          </FormControl>

          {/* Botão Adicionar */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAdicionar}
              disabled={!isFormularioValido()}
              sx={{
                textTransform: 'none',
                backgroundColor: isFormularioValido() ? '#1976d2' : undefined,
              }}
            >
              Adicionar
            </Button>
          </Box>

        </Box>
      </ContainerFormFormik>

      {/* SEÇÃO: Registros Cadastrados */}
      <ContainerFormFormik title={`Registros Cadastrados (${programas.length} registro${programas.length !== 1 ? 's' : ''})`}>
        <Box>
          {programas.length === 0 ? (
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 1,
                p: 4,
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}
            >
              <Typography variant="body1" color="textSecondary">
                Nenhum registro cadastrado ainda.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Preencha o formulário acima para adicionar.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Programa</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Parceiro</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Beneficiados</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo da Comunidade</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '100px' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programas.map((prog) => (
                    <TableRow key={prog.id} hover>
                      <TableCell>{prog.programa}</TableCell>
                      <TableCell>{prog.parceiros.join(', ')}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{prog.beneficiados}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{prog.tipos_comunidade.join(', ')}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          color="error"
                          onClick={() => removerPrograma(prog.id!)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </ContainerFormFormik>

      <ButtonForm
        disabled={!podeAvancar}
        onBack={handleVoltar}
        onAdvanced={handleAvancar}
        stepAtual={stepAtual}
        totalSteps={totalSteps}
      />
    </>
  );
};

export default Step7;
